import { MultiselectAutosuggest } from '@seek/multiselect-autosuggest';
import { useTranslations } from '@vocab/react';
import {
  RadioGroup,
  RadioItem,
  Dropdown,
  Box,
  Stack,
  Checkbox,
  Tag,
  Text,
} from 'braid-design-system';
import type React from 'react';
import { useState, useMemo, useEffect } from 'react';

import translations from '@seek/rr-caja/src/.vocab';

import { type AvailableAnswer, SelectionType } from '../../sdk';
import { AnswerRange, type AnswerRangeType } from '../AnswerRange/AnswerRange';

import { fuzzyFilter } from './fuzzyFilter';

import * as styles from './styles.css';

export type AnswerChangeHandler = (
  answer: ToggleAnswer | ToggleRangeAnswer,
) => void;

export interface AutosuggestAnswer extends AvailableAnswer {
  name: string;
}

type ToggleAnswer = {
  questionId: string;
  answerId: string;
  isSelected: boolean;
};

type ToggleRangeAnswer = {
  questionId: string;
  answerId: string;
  type: AnswerRangeType;
};

interface AnswerListProps {
  answers: AvailableAnswer[];
  questionId: string;
  selectionType: SelectionType;
  toggleAnswerSelection: AnswerChangeHandler;
  isDropdownExperimentEnabled: boolean;
}

type Handlers = {
  [key in SelectionType]: React.FunctionComponent<AnswerListProps>;
};

const CheckboxSelection = ({
  answers,
  questionId,
  toggleAnswerSelection,
  isDropdownExperimentEnabled,
}: AnswerListProps) => {
  const { t } = useTranslations(translations);

  const [selectedItems, setSelectedItems] = useState<AutosuggestAnswer[]>([]);

  useEffect(() => {
    setSelectedItems(
      answers
        .filter((answer) => answer.preferred)
        .map((answer) => ({
          ...answer,
          name: answer.text,
        })),
    );
  }, [answers]);

  const [searchText, setSearchText] = useState('');

  const autosuggestAnswers: AutosuggestAnswer[] = useMemo(() => {
    if (searchText) {
      return fuzzyFilter({
        items: answers.map((answer) => ({
          ...answer,
          name: answer.text,
        })),
        searchText,
      });
    }

    return answers.map((answer) => ({
      ...answer,
      name: answer.text,
    }));
  }, [searchText, answers]);

  return isDropdownExperimentEnabled ? (
    <MultiselectAutosuggest
      id="answer-multi-select"
      label="Answers"
      hideLabel={true}
      placeholder={t('answer.multiSelect.placeholder')}
      clearLabel="Clear"
      size="small"
      suggestions={autosuggestAnswers}
      selectedItems={selectedItems}
      searchText={searchText}
      setSearchText={setSearchText}
      renderSuggestions={({ getItemProps, highlightedIndex }) =>
        autosuggestAnswers.length > 0 ? (
          <>
            {autosuggestAnswers.map((autosuggestAnswer, index) => {
              const isSelected = selectedItems.some(
                (selectedItem) => selectedItem.id === autosuggestAnswer.id,
              );
              const suggestionId = `suggestion-${autosuggestAnswer.id}`;
              return (
                <Box
                  key={autosuggestAnswer.id}
                  padding="xsmall"
                  cursor="pointer"
                  background={
                    highlightedIndex === index
                      ? 'formAccentSoftHover'
                      : 'surface'
                  }
                  {...getItemProps({
                    item: autosuggestAnswer,
                  })}
                >
                  <Checkbox
                    aria-labelledby={`Suggestion ${autosuggestAnswer.name}`}
                    checked={isSelected}
                    id={suggestionId}
                    size="small"
                    onChange={(e) => {
                      e.stopPropagation();
                    }}
                    label={autosuggestAnswer.name}
                  />
                </Box>
              );
            })}
          </>
        ) : (
          <Box padding="small" background="surface">
            <Text size="small">{t('answer.multiSelect.noResult')}</Text>
          </Box>
        )
      }
      renderSelectedItems={() => (
        <Box
          display="flex"
          alignItems="center"
          flexWrap="wrap"
          width={selectedItems.length > 0 ? 'full' : undefined}
          className={styles.tagList}
        >
          {selectedItems.map((selectedItem: AutosuggestAnswer) => (
            <Box flexWrap="nowrap" overflow="hidden" key={selectedItem.id}>
              <Tag
                size="small"
                onClear={() => {
                  toggleAnswerSelection({
                    questionId,
                    answerId: selectedItem.id,
                    isSelected: false,
                  });
                  setSelectedItems(
                    selectedItems.filter(
                      (itemToFilter: AutosuggestAnswer) =>
                        itemToFilter.id !== selectedItem.id,
                    ),
                  );
                }}
                clearLabel={`Clear "${selectedItem.name}"`}
              >
                {selectedItem.name}
              </Tag>
            </Box>
          ))}
        </Box>
      )}
      onClickSuggestionItem={(item) => {
        if (
          selectedItems.some(
            (selectedItem: AutosuggestAnswer) => selectedItem.id === item.id,
          )
        ) {
          toggleAnswerSelection({
            questionId,
            answerId: item.id,
            isSelected: false,
          });
          setSelectedItems(
            selectedItems.filter(
              (itemToFilter: AutosuggestAnswer) => itemToFilter.id !== item.id,
            ),
          );
        } else {
          toggleAnswerSelection({
            questionId,
            answerId: item.id,
            isSelected: true,
          });
          setSelectedItems((prev: AutosuggestAnswer[]) => [...prev, item]);
        }
      }}
      onItemsSelected={(items) => {
        items.forEach((item) => {
          if (
            !selectedItems.find(
              (selectedItem: AutosuggestAnswer) => selectedItem.id === item.id,
            )
          ) {
            toggleAnswerSelection({
              questionId,
              answerId: item.id,
              isSelected: true,
            });
          }
        });
        selectedItems.forEach((selectedItem: AutosuggestAnswer) => {
          if (!items.find((item) => selectedItem.id === item.id)) {
            toggleAnswerSelection({
              questionId,
              answerId: selectedItem.id,
              isSelected: false,
            });
          }
        });
        setSelectedItems(items ?? []);
      }}
    />
  ) : (
    <Stack space="small">
      {answers.map((answer) => (
        <Checkbox
          key={answer.id}
          id={answer.id}
          size="small"
          checked={answer.preferred}
          label={answer.text}
          onChange={() =>
            toggleAnswerSelection({
              questionId,
              answerId: answer.id,
              isSelected: !answer.preferred,
            })
          }
          message={false}
          reserveMessageSpace={false}
        />
      ))}
    </Stack>
  );
};

const RadioSelection = ({
  answers,
  questionId,
  toggleAnswerSelection,
  isDropdownExperimentEnabled,
}: AnswerListProps) => {
  const { t } = useTranslations(translations);
  return isDropdownExperimentEnabled ? (
    <Dropdown
      id={questionId}
      aria-label={questionId}
      value={answers.filter((a) => a.preferred)[0]?.id}
      placeholder={t('custom.answerTypeDropdown.placeholder')}
      onChange={({ currentTarget: { value } }) => {
        toggleAnswerSelection({
          questionId,
          answerId: value,
          isSelected: !answers.find((a) => a.id === value)?.preferred,
        });
      }}
    >
      {answers.map((answer) => (
        <option key={answer.id} value={answer.id}>
          {answer.text}
        </option>
      ))}
    </Dropdown>
  ) : (
    <RadioGroup
      id={questionId}
      aria-label={questionId}
      value={answers.filter((a) => a.preferred)[0]?.id}
      onChange={({ currentTarget: { value } }) => {
        toggleAnswerSelection({
          questionId,
          answerId: value,
          isSelected: !answers.find((a) => a.id === value)?.preferred,
        });
      }}
      size="small"
    >
      {answers.map((answer) => (
        <RadioItem key={answer.id} label={answer.text} value={answer.id} />
      ))}
    </RadioGroup>
  );
};

const RangeSelection = ({
  answers,
  questionId,
  toggleAnswerSelection,
}: AnswerListProps) => (
  <AnswerRange
    questionId={questionId}
    answers={answers}
    onChange={(answerId, type) =>
      toggleAnswerSelection({
        questionId,
        answerId,
        type,
      })
    }
  />
);

const RegisteredHandlers: Handlers = {
  [SelectionType.Radio]: RadioSelection,
  [SelectionType.Checkbox]: CheckboxSelection,
  [SelectionType.Range]: RangeSelection,
  [SelectionType.FreeText]: () => null,
};

const AnswerList: React.FunctionComponent<AnswerListProps> = (props) =>
  RegisteredHandlers[props.selectionType](props);

export default AnswerList;
